import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Alert, Anchor, Badge, Code, Group, Paper, SimpleGrid, Skeleton as MSkeleton, Stack, Table, Text,
} from '@mantine/core'
import { IconAlertCircle, IconHourglassOff, IconInfoCircle, IconListCheck, IconPlugConnectedX } from '@tabler/icons-react'
import { EmptyState, PageTitle, SectionCard } from '@lighthouse/design'
import { RestrictedState } from '../components/access/RestrictedState'
import { ReportAccessDrawer } from '../components/access/ReportAccessDrawer'
import { CartographerLink } from '../components/cartographer/CartographerLink'
import {
  GROUP_MEMBERS_NOT_RESOLVABLE, PERMISSION_AUDIT_PATH, PERSONAL_WORKSPACE_BADGE, REPORT_ARCHIVED_BADGE,
  archivedGrantState, businessOwnerLabel, expiryText, findingSectionId, findingTally, grantKindLabel,
  grantReportStatusBadge, grantTargetCell, grantedByLabel, groupOrRoleCaption, inactiveListedCount, inactiveSplitText,
  inactivityWithheldNotice,
  lapseText, lastRunLabel, measurementSource, pageFetchEnabled, parseFindingParam, principalLabel,
  reachSubtitle, recipientLabel, scheduleReasonLabel, scrollReady, sectionErrorCopy, sectionView, whenUtc,
  workspaceCell, countOrDash,
} from '../components/governance/governanceFormat'
import type { SectionView } from '../components/governance/governanceFormat'
import { useGovernanceFinding } from '../hooks/useGovernance'
import { useLighthouseAdmin } from '../hooks/useLighthouseAdmin'
import { isApiError } from '../services/apiError'
import { NOT_MEASURED, errorKind, exactNumber } from '../utils/format'
import { reportDetailPath } from '../utils/reportDetailPath'
import type { ReportDetailTab } from '../utils/reportDetailPath'
import type {
  ArchivedReportGrant, GovernanceFinding, GovernanceOverview, GrantExpiry, InactiveGrantHolder, OwnershipGap,
  ReportReach, ScheduleIdentity,
} from '../types/governance'

/** What RestrictedState names when the caller lacks Lighthouse.Admin. */
const SURFACE = 'Governance Queue'

/**
 * Governance Queue (Muster) — what access exists that nobody is watching.
 *
 * `GET api/observatory/governance` shipped with build-plan item #6 and nothing
 * consumed it. This page is that endpoint and no more: no controller, no
 * migration, nothing recomputed in the browser.
 *
 * Five decisions shape it:
 *
 *  1. **One request per finding group, always** (services/governanceApi.ts). Five
 *     of the seven groups read columns added by AddGrantExpiryAndNotes, unapplied
 *     in Staging and Production; an unfiltered request would 500 all seven. So each
 *     section is its own query with its own loading, error and empty state, and
 *     the two groups that can answer everywhere — ownership and schedules — do.
 *  2. **Read-only, visibly** (doc 17, amendment A1, ruled as D6a). Every remedy
 *     mutates Cartographer's access algebra behind Cartographer's gate — restoring
 *     an archived report included — so the page hands off to where each one lives
 *     and proxies none of them.
 *  3. **Null is never empty.** A null list was not computed; an empty one was
 *     computed and found nothing; a 401, 403 or 500 is neither. Only the second
 *     says "None found" — a failed request must never read as "queue is clear".
 *  4. **Counts, never rates.** The denominators the server knows are served as
 *     their own numbers in the measurement strip; no percentage appears.
 *  5. **Ordered as the server orders.** Still-delivering ownership gaps first,
 *     reach by the larger of its two people counts, archived-report grants that
 *     would return on restore before lapsed ones; the page sorts nothing.
 *  6. **People and groups are never one figure** (D7(a)). Group and role grants
 *     are counted, captioned "group members not resolvable", and never added to
 *     the people who can actually open a report.
 *  7. **A report's name opens Report Detail on the tab its finding is about**
 *     (`/registry/:reportId`, D5): an ownership gap on Governance, a grant (live,
 *     lapsed or on an archived report) and reach on Access, a schedule on
 *     Scheduling. Where a row could already open the access explanation in place,
 *     its Access button still does. Inactive holders are people, not reports, and
 *     link nowhere.
 *
 * DISCLOSURE: named principals and grant provenance across every workspace,
 * personal ones included — accepted by doc 17 §4 under Lighthouse.Admin only.
 * Recipient addresses are never served; only their count.
 */
export function GovernanceQueuePage() {
  const gate = useLighthouseAdmin()
  const enabled = pageFetchEnabled(gate)

  // All seven mount together and each fetches its own group. On `denied` none of
  // them fetches (enabled: false) and the page is the restricted state alone.
  const ownership = useGovernanceFinding('ownership', { enabled })
  const expiring = useGovernanceFinding('expiring', { enabled })
  const lapsed = useGovernanceFinding('lapsed', { enabled })
  const archived = useGovernanceFinding('archived', { enabled })
  const reach = useGovernanceFinding('reach', { enabled })
  const schedules = useGovernanceFinding('schedules', { enabled })
  const inactive = useGovernanceFinding('inactive', { enabled })

  const [searchParams] = useSearchParams()
  const focus = parseFindingParam(searchParams.get('finding'))
  const [drawerReportId, setDrawerReportId] = useState<string | null>(null)

  // `?finding=<key>` scrolls to that section — ONCE, and only after every section
  // above it (and it) has settled. Every header renders in every state, so the
  // anchor exists on the first paint; but scrolling then lands on a target that
  // the rows loading above it immediately push off the screen.
  const ready = scrollReady(focus, {
    ownership: ownership.isLoading,
    expiring: expiring.isLoading,
    lapsed: lapsed.isLoading,
    archived: archived.isLoading,
    reach: reach.isLoading,
    schedules: schedules.isLoading,
    inactive: inactive.isLoading,
  })
  const scrolledTo = useRef<GovernanceFinding | null>(null)
  useEffect(() => {
    if (!focus || !enabled || !ready || scrolledTo.current === focus) return
    scrolledTo.current = focus
    document.getElementById(findingSectionId(focus))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [focus, enabled, ready])

  if (gate.state === 'denied') {
    return (
      <Stack gap="lg" pb="xl">
        <Header />
        <RestrictedState surface={SURFACE} reason={gate.reason} />
      </Stack>
    )
  }

  // One 403 is every 403: the policy is class-level, so the page says so once
  // rather than seven times.
  const refused = [ownership, expiring, lapsed, archived, reach, schedules, inactive]
    .find((query) => query.isError && errorKind(query.error) === 'forbidden')
  if (refused) {
    return (
      <Stack gap="lg" pb="xl">
        <Header />
        <RestrictedState surface={SURFACE} reason={isApiError(refused.error) ? refused.error.detail : null} />
      </Stack>
    )
  }

  // Ownership and schedules first: they read no migration-dependent column, so
  // they are the responses most likely to exist.
  const source = measurementSource([
    ownership.data, schedules.data, expiring.data, lapsed.data, archived.data, reach.data, inactive.data,
  ])
  const anyPending = [ownership, expiring, lapsed, archived, reach, schedules, inactive].some((q) => q.isLoading)
  const openReport = (reportId: string) => setDrawerReportId(reportId)

  return (
    <Stack gap="lg" pb="xl">
      <Header />
      <ReadOnlyBanner />
      {source && <InactivityNotice source={source} />}
      <MeasurementStrip source={source} pending={anyPending} />

      <OwnershipSection
        view={sectionView(ownership, (d) => ({ rows: d.ownershipGaps, count: d.ownershipGapCount }))}
        onOpenReport={openReport}
      />
      <ExpiringSection
        view={sectionView(expiring, (d) => ({ rows: d.grantsNearingExpiry, count: d.grantsNearingExpiryCount }))}
        withinDays={expiring.data?.expiringWithinDays ?? source?.expiringWithinDays ?? null}
      />
      <LapsedSection
        view={sectionView(lapsed, (d) => ({ rows: d.lapsedGrants, count: d.lapsedGrantCount }))}
      />
      <ArchivedSection
        view={sectionView(archived, (d) => ({
          rows: d.grantsOnArchivedReports ?? null, count: d.grantsOnArchivedReportsCount ?? null,
        }))}
        onOpenReport={openReport}
      />
      <ReachSection
        view={sectionView(reach, (d) => ({ rows: d.broadlyReachableReports, count: d.sharedReportCount }))}
        response={reach.data}
        onOpenReport={openReport}
      />
      <SchedulesSection
        view={sectionView(schedules, (d) => ({
          rows: d.schedulesRunningAsNonOwner, count: d.schedulesRunningAsNonOwnerCount,
        }))}
      />
      <InactiveSection
        view={sectionView(inactive, (d) => ({ rows: d.inactiveGrantHolders, count: inactiveListedCount(d) }))}
        response={inactive.data}
        thresholdDays={inactive.data?.inactivityThresholdDays ?? source?.inactivityThresholdDays ?? null}
      />

      <ReportAccessDrawer reportId={drawerReportId} onClose={() => setDrawerReportId(null)} />
    </Stack>
  )
}

function Header() {
  return (
    <PageTitle subtitle="Muster — reports with no accountable owner, grants about to lapse, and schedules running under nobody. Read-only.">
      Governance Queue
    </PageTitle>
  )
}

/** Amendment A1, said where it applies: this version reads, and Cartographer acts. */
function ReadOnlyBanner() {
  const navigate = useNavigate()
  return (
    <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light" title="Read-only in this version">
      <Stack gap={6}>
        <Text size="sm">
          Reassigning an owner, running the orphan sweep and revoking a grant happen in Cartographer&rsquo;s
          Governance page, and restoring an archived report on its Archive page, both of which need the
          Cartographer.Admin role — this page holds no Cartographer role and proxies none of them.
        </Text>
        <Group gap="md" wrap="wrap">
          <CartographerLink path="/governance" label="Governance" />
          <Anchor component="button" type="button" size="sm" onClick={() => navigate(PERMISSION_AUDIT_PATH)}>
            Revoked grants in the audit trail →
          </Anchor>
        </Group>
        <Text size="xs" c="dimmed">
          A revocation is a hard delete: the grant row is removed and no RevokedAt is kept anywhere, so the audit
          trail&rsquo;s permission changes are its only record. Nothing below can show one.
        </Text>
      </Stack>
    </Alert>
  )
}

/** The one top notice when the inactivity list is withheld — said once, never as "none". */
function InactivityNotice({ source }: { source: GovernanceOverview }) {
  const notice = inactivityWithheldNotice(source)
  if (!notice) return null
  return (
    <Alert icon={<IconHourglassOff size={16} />} color="gray" variant="light" title="Inactivity cannot be judged yet">
      <Text size="sm">{notice}</Text>
    </Alert>
  )
}

/** The denominators the server knows, as counts. Never a percentage. */
function MeasurementStrip({ source, pending }: { source: GovernanceOverview | undefined; pending: boolean }) {
  const cell = (label: string, value: number | undefined) => (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{label}</Text>
      {source === undefined && pending
        ? <MSkeleton height={22} width={60} />
        : <Text size="lg" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>{countOrDash(value)}</Text>}
    </Stack>
  )

  return (
    <Paper p="md" withBorder style={{ backgroundColor: 'var(--color-bg-surface)' }}>
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        {cell('Reports examined', source?.reportsExamined)}
        {cell('Report grants examined', source?.reportGrantsExamined)}
        {cell('Workspace memberships examined', source?.workspaceMembershipsExamined)}
      </SimpleGrid>
      <Text size="xs" c="dimmed" mt="xs">
        Every Active and AttentionRequired report, and every grant and membership row, live and lapsed alike. These
        are what each finding below was drawn from.
      </Text>
    </Paper>
  )
}

// ---------------------------------------------------------------- the section frame

/**
 * One finding group: its header in every state, then exactly one of loading,
 * restricted, error, withheld, "None found" or its rows.
 */
function FindingSection<T>({
  finding, title, subtitle, sectionName, view, notComputed, footer, children,
}: {
  finding: GovernanceFinding
  title: string
  subtitle: ReactNode
  sectionName: string
  view: SectionView<T>
  notComputed?: ReactNode
  footer?: ReactNode
  children: (rows: T[]) => ReactNode
}) {
  const tally = view.kind === 'rows'
    ? findingTally(view.rows.length, view.count)
    : view.kind === 'empty' ? findingTally(0, view.count) : null

  return (
    <div id={findingSectionId(finding)} style={{ scrollMarginTop: 72 }}>
      <SectionCard
        title={title}
        subtitle={subtitle}
        flush
        action={tally != null ? <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>{tally}</Text> : undefined}
      >
        <SectionBody finding={finding} sectionName={sectionName} view={view} notComputed={notComputed}>
          {children}
        </SectionBody>
        {footer && <div style={{ padding: '10px 16px', borderTop: '1px solid var(--color-border)' }}>{footer}</div>}
      </SectionCard>
    </div>
  )
}

function SectionBody<T>({
  finding, sectionName, view, notComputed, children,
}: {
  finding: GovernanceFinding
  sectionName: string
  view: SectionView<T>
  notComputed?: ReactNode
  children: (rows: T[]) => ReactNode
}) {
  switch (view.kind) {
    case 'loading':
      return (
        <Stack gap={8} p="md">
          <MSkeleton height={14} />
          <MSkeleton height={14} />
          <MSkeleton height={14} width="60%" />
        </Stack>
      )
    case 'restricted':
      return <div style={{ padding: 16 }}><RestrictedState compact surface={SURFACE} reason={view.reason} /></div>
    case 'error': {
      const copy = sectionErrorCopy(finding, sectionName, view.errorKind)
      return (
        <EmptyState
          icon={view.errorKind === 'network'
            ? <IconPlugConnectedX size={28} stroke={1.5} />
            : <IconAlertCircle size={28} stroke={1.5} />}
          title={copy.title}
          message={copy.message}
          action={view.summary ? <Code style={{ overflowWrap: 'anywhere' }}>{view.summary}</Code> : undefined}
        />
      )
    }
    case 'not-computed':
      return (
        <div style={{ padding: 16 }}>
          {notComputed ?? (
            <Text size="sm" c="dimmed">
              {NOT_MEASURED} Not computed by this response, so nothing is known here — which is not the same as none.
            </Text>
          )}
        </div>
      )
    case 'empty':
      return (
        <Group gap={8} p="md" wrap="nowrap">
          <IconListCheck size={16} stroke={1.6} style={{ color: 'var(--color-text-disabled)', flexShrink: 0 }} />
          <Text size="sm" c="dimmed">None found.</Text>
        </Group>
      )
    case 'rows':
      return <>{children(view.rows)}</>
  }
}

// ---------------------------------------------------------------- cells

function Mono({ children }: { children: ReactNode }) {
  return <Text size="xs" ff="monospace" style={{ overflowWrap: 'anywhere' }}>{children}</Text>
}

function Dash() {
  return <Text span size="sm" c="dimmed">{NOT_MEASURED}</Text>
}

function WorkspaceCell({ row }: { row: { workspaceId: string | null; workspaceName: string | null; isPersonalWorkspace: boolean } }) {
  const cell = workspaceCell(row)
  return (
    <Stack gap={2} align="flex-start">
      <Text size="sm" c={row.workspaceId == null ? 'dimmed' : undefined}>{cell.text}</Text>
      {cell.personalBadge && <Badge size="xs" variant="outline" color="gray">{PERSONAL_WORKSPACE_BADGE}</Badge>}
    </Stack>
  )
}

/**
 * A report's name, opening its Report Detail on `tab`. A button that navigates (a
 * <tr> takes no focus, and every name on this page has always been a button); the
 * target is also carried as data, so a rendered row says where it goes.
 */
function ReportButton({ name, reportId, tab }: { name: string; reportId: string; tab: ReportDetailTab }) {
  const navigate = useNavigate()
  const to = reportDetailPath(reportId, tab)
  return (
    <Anchor component="button" type="button" size="sm" ta="left" fw={500} data-report-detail={to} onClick={() => navigate(to)}>
      {name}
    </Anchor>
  )
}

/** The access explanation in place, over this page: the drawer a report row has always opened. */
function AccessButton({ name, onClick }: { name: string; onClick: () => void }) {
  return (
    <Anchor
      component="button"
      type="button"
      size="xs"
      data-action="access"
      onClick={onClick}
      aria-label={`Who can reach ${name}, and why`}
    >
      Access
    </Anchor>
  )
}

function PrincipalCell({ displayName, id }: { displayName: string | null; id: string }) {
  const label = principalLabel(displayName, id)
  return label === id ? <Mono>{id}</Mono> : <Text size="sm">{label}</Text>
}

// ---------------------------------------------------------------- (1) ownership gaps

function OwnershipSection({ view, onOpenReport }: { view: SectionView<OwnershipGap>; onOpenReport: (id: string) => void }) {
  return (
    <FindingSection
      finding="ownership"
      title="Ownership gaps"
      sectionName="ownership gaps"
      subtitle="Reports the orphan sweep flagged AttentionRequired — still-delivering first, as the server orders them. A report's name opens its governance; Access shows who can reach it."
      view={view}
    >
      {(rows) => (
        <Table.ScrollContainer minWidth={1100}>
          <Table striped highlightOnHover verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Report</Table.Th>
                <Table.Th>Workspace</Table.Th>
                <Table.Th>Business owner</Table.Th>
                <Table.Th>Maintainer</Table.Th>
                <Table.Th>Created by (history)</Table.Th>
                <Table.Th>Flagged</Table.Th>
                <Table.Th>Reason</Table.Th>
                <Table.Th>Last run</Table.Th>
                <Table.Th ta="right">Active schedules</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.reportId}>
                  <Table.Td>
                    <Stack gap={2} align="flex-start">
                      <ReportButton name={row.name} reportId={row.reportId} tab="governance" />
                      <AccessButton name={row.name} onClick={() => onOpenReport(row.reportId)} />
                    </Stack>
                  </Table.Td>
                  <Table.Td><WorkspaceCell row={row} /></Table.Td>
                  <Table.Td>
                    {row.businessOwnerPrincipalId == null
                      ? <Text size="sm" fw={600}>{businessOwnerLabel(null)}</Text>
                      : <Mono>{row.businessOwnerPrincipalId}</Mono>}
                  </Table.Td>
                  <Table.Td>{row.maintainerPrincipalId == null ? <Dash /> : <Mono>{row.maintainerPrincipalId}</Mono>}</Table.Td>
                  <Table.Td>
                    <Stack gap={2}>
                      <Text size="sm">{row.creatorSnapshot ?? NOT_MEASURED}</Text>
                      {row.authorUserId && <Text size="xs" c="dimmed" ff="monospace" style={{ overflowWrap: 'anywhere' }}>{row.authorUserId}</Text>}
                    </Stack>
                  </Table.Td>
                  <Table.Td><Text size="sm">{whenUtc(row.attentionRequiredAt)}</Text></Table.Td>
                  <Table.Td><Text size="sm">{row.attentionReason ?? NOT_MEASURED}</Text></Table.Td>
                  <Table.Td><Text size="sm">{lastRunLabel(row.lastRunAt)}</Text></Table.Td>
                  <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{exactNumber(row.activeScheduleCount)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </FindingSection>
  )
}

// ---------------------------------------------------------------- (2) and (3) grants

function GrantTable({ rows, lapsed }: { rows: GrantExpiry[]; lapsed: boolean }) {
  return (
    <Table.ScrollContainer minWidth={1100}>
      <Table striped highlightOnHover verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Kind</Table.Th>
            <Table.Th>Principal</Table.Th>
            <Table.Th>Role</Table.Th>
            <Table.Th>Report or workspace</Table.Th>
            <Table.Th>Granted by</Table.Th>
            <Table.Th>Granted</Table.Th>
            <Table.Th>Note</Table.Th>
            <Table.Th>{lapsed ? 'Lapsed' : 'Expires'}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((grant, index) => {
            const target = grantTargetCell(grant)
            // `?? null`: an API predating the field omits it, and that is "not stated", not Active.
            const statusBadge = grantReportStatusBadge(grant.reportStatus ?? null)
            return (
              <Table.Tr key={`${grant.grantKind}-${grant.principalId}-${grant.reportId ?? grant.workspaceId ?? ''}-${index}`}>
                <Table.Td><Text size="sm">{grantKindLabel(grant.grantKind)}</Text></Table.Td>
                <Table.Td><PrincipalCell displayName={grant.principalDisplayName} id={grant.principalId} /></Table.Td>
                <Table.Td><Text size="sm">{grant.role}</Text></Table.Td>
                <Table.Td>
                  <Stack gap={2} align="flex-start">
                    {/* A report grant opens its report's Access tab; a membership covers a workspace, not a report. */}
                    {grant.reportId != null && !target.unresolved
                      ? <ReportButton name={target.primary} reportId={grant.reportId} tab="access" />
                      : <Text size="sm" c={target.unresolved ? 'dimmed' : undefined}>{target.primary}</Text>}
                    {target.secondary && <Text size="xs" c="dimmed">{target.secondary}</Text>}
                    {target.personalBadge && <Badge size="xs" variant="outline" color="gray">{PERSONAL_WORKSPACE_BADGE}</Badge>}
                    {statusBadge && <Badge size="xs" variant="light" color="gray">{statusBadge}</Badge>}
                  </Stack>
                </Table.Td>
                <Table.Td><Text size="sm">{grantedByLabel(grant)}</Text></Table.Td>
                <Table.Td><Text size="sm">{whenUtc(grant.grantedAt)}</Text></Table.Td>
                <Table.Td><Text size="sm">{grant.grantNote ?? NOT_MEASURED}</Text></Table.Td>
                <Table.Td>
                  <Stack gap={2}>
                    <Text size="sm" fw={500}>{lapsed ? lapseText(grant.expiresAt) : expiryText(grant.expiresAt)}</Text>
                    <Text size="xs" c="dimmed">{whenUtc(grant.expiresAt)}</Text>
                  </Stack>
                </Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  )
}

function ExpiringSection({ view, withinDays }: { view: SectionView<GrantExpiry>; withinDays: number | null }) {
  const window = withinDays == null ? NOT_MEASURED : `${withinDays} days`
  return (
    <FindingSection
      finding="expiring"
      title="Grants nearing expiry"
      sectionName="grants nearing expiry"
      subtitle={`Live access that stops within ${window} — the action is to renew it or let it lapse deliberately.`}
      view={view}
    >
      {(rows) => <GrantTable rows={rows} lapsed={false} />}
    </FindingSection>
  )
}

function LapsedSection({ view }: { view: SectionView<GrantExpiry> }) {
  return (
    <FindingSection
      finding="lapsed"
      title="Housekeeping — these confer nothing"
      sectionName="lapsed grants"
      subtitle="Grants whose expiry has passed. Every access check honours expiry, so none of these reaches anything; they are listed only because no job ever deletes them."
      view={view}
    >
      {(rows) => <GrantTable rows={rows} lapsed />}
    </FindingSection>
  )
}

// ---------------------------------------------------------------- (4) grants on archived reports

/**
 * D7(b): grants over reports somebody archived — listed apart, never counted as
 * live, never hidden. Not latent: grantees can still open the report read-only,
 * definition included; run, export and schedule return on restore. Restoring is
 * Cartographer's (Archive page) and this section only hands off to it (D6a).
 */
function ArchivedSection({
  view, onOpenReport,
}: {
  view: SectionView<ArchivedReportGrant>
  onOpenReport: (id: string) => void
}) {
  return (
    <FindingSection
      finding="archived"
      title="Grants on archived reports — view-only"
      sectionName="grants on archived reports"
      subtitle="Direct grants over reports somebody archived. Not dormant: grantees can still open these reports read-only, definition included, and run, export and schedule come back the day a report is restored. Listed apart and never counted as live access."
      view={view}
      footer={
        <Group gap="md" wrap="wrap">
          <Text size="xs" c="dimmed">
            Restoring a report is done in Cartographer; this page acts on no Cartographer data.
          </Text>
          <CartographerLink path="/archive" label="Archive" />
        </Group>
      }
    >
      {(rows) => (
        <Table.ScrollContainer minWidth={1100}>
          <Table striped highlightOnHover verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Report</Table.Th>
                <Table.Th>Principal</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>Granted by</Table.Th>
                <Table.Th>Note</Table.Th>
                <Table.Th>Archived</Table.Th>
                <Table.Th>What it confers</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((grant, index) => {
                const workspace = workspaceCell(grant)
                return (
                  <Table.Tr key={`${grant.reportId}-${grant.principalId}-${index}`}>
                    <Table.Td>
                      <Stack gap={2} align="flex-start">
                        {grant.reportName == null
                          ? <Text size="sm" c="dimmed">Unresolved report</Text>
                          : (
                            <>
                              <ReportButton name={grant.reportName} reportId={grant.reportId} tab="access" />
                              <AccessButton name={grant.reportName} onClick={() => onOpenReport(grant.reportId)} />
                            </>
                          )}
                        <Text size="xs" c="dimmed">{workspace.text}</Text>
                        <Group gap={4}>
                          <Badge size="xs" variant="light" color="gray">{REPORT_ARCHIVED_BADGE}</Badge>
                          {workspace.personalBadge && <Badge size="xs" variant="outline" color="gray">{PERSONAL_WORKSPACE_BADGE}</Badge>}
                        </Group>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <PrincipalCell displayName={grant.principalDisplayName} id={grant.principalId} />
                        {grant.principalType !== 'User' && <Text size="xs" c="dimmed">{grant.principalType}</Text>}
                      </Stack>
                    </Table.Td>
                    <Table.Td><Text size="sm">{grant.role}</Text></Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="sm">{grantedByLabel(grant)}</Text>
                        <Text size="xs" c="dimmed">{whenUtc(grant.grantedAt)}</Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td><Text size="sm">{grant.grantNote ?? NOT_MEASURED}</Text></Table.Td>
                    <Table.Td><Text size="sm">{whenUtc(grant.archivedAt)}</Text></Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="sm" fw={500}>{archivedGrantState(grant)}</Text>
                        {grant.expiresAt != null && (
                          <Text size="xs" c="dimmed">
                            {grant.isLapsed ? lapseText(grant.expiresAt) : expiryText(grant.expiresAt)}
                          </Text>
                        )}
                      </Stack>
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </FindingSection>
  )
}

// ---------------------------------------------------------------- (5) reach

function ReachSection({
  view, response, onOpenReport,
}: {
  view: SectionView<ReportReach>
  response: GovernanceOverview | undefined
  onOpenReport: (id: string) => void
}) {
  // Every figure from the reach response itself: a count from one request over a
  // denominator from another would be two instants dressed as one ratio. A
  // failed or refused section states no figure at all.
  const failed = view.kind === 'error' || view.kind === 'restricted'
  const subtitle = reachSubtitle({
    withPeople: failed ? null : response?.reportsSharedWithPeopleCount,
    onlyGroupOrRole: failed ? null : response?.reportsWithOnlyGroupOrRoleGrantsCount,
    examined: response?.reportsExamined,
  })
  return (
    <FindingSection
      finding="reach"
      title="Reach"
      sectionName="report reach"
      subtitle={subtitle}
      view={view}
      footer={
        <Stack gap={4}>
          <Text size="xs" c="dimmed">
            No count includes the owner, the maintainer or Cartographer.Admin holders, and the workspace count
            excludes the workspace owner&rsquo;s own membership. There is no &ldquo;too broad&rdquo; line here: that
            is a judgement about the team, not a fact about the row.
          </Text>
          <Text size="xs" c="dimmed">
            <strong>Group or role grants:</strong> {groupOrRoleCaption(response)}.
          </Text>
        </Stack>
      }
    >
      {(rows) => (
        <Table.ScrollContainer minWidth={860}>
          <Table striped highlightOnHover verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Report</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Workspace</Table.Th>
                <Table.Th ta="right">People granted</Table.Th>
                <Table.Th ta="right">
                  <Stack gap={0} align="flex-end">
                    <span>Group or role grants</span>
                    <Text span size="xs" c="dimmed" fw={400}>{GROUP_MEMBERS_NOT_RESOLVABLE}</Text>
                  </Stack>
                </Table.Th>
                <Table.Th ta="right">Workspace members</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.reportId}>
                  <Table.Td>
                    <Stack gap={2} align="flex-start">
                      <ReportButton name={row.name} reportId={row.reportId} tab="access" />
                      <AccessButton name={row.name} onClick={() => onOpenReport(row.reportId)} />
                    </Stack>
                  </Table.Td>
                  <Table.Td><Text size="sm">{row.status}</Text></Table.Td>
                  <Table.Td><WorkspaceCell row={row} /></Table.Td>
                  <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{exactNumber(row.userGrantCount)}</Table.Td>
                  <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    <Text span size="sm" c={row.groupOrRoleGrantCount === 0 ? 'dimmed' : undefined}>
                      {exactNumber(row.groupOrRoleGrantCount)}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{exactNumber(row.workspaceMemberCount)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </FindingSection>
  )
}

// ---------------------------------------------------------------- (6) schedules

function SchedulesSection({ view }: { view: SectionView<ScheduleIdentity> }) {
  const navigate = useNavigate()
  return (
    <FindingSection
      finding="schedules"
      title="Schedules running as a non-owner"
      sectionName="schedules running as a non-owner"
      subtitle="Active schedules delivering under an identity nobody is accountable for. A scheduled run has no interactive caller, so its rows are scoped to the stored run-as identity."
      view={view}
      footer={
        <Group gap="md" wrap="wrap">
          <Text size="xs" c="dimmed">To pause or edit a schedule:</Text>
          <CartographerLink path="/schedules" label="Town Crier" />
          <Anchor component="button" type="button" size="sm" onClick={() => navigate('/schedules-load')}>
            See its load on the shared workers →
          </Anchor>
        </Group>
      }
    >
      {(rows) => (
        <Table.ScrollContainer minWidth={1100}>
          <Table striped highlightOnHover verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Schedule</Table.Th>
                <Table.Th>Report</Table.Th>
                <Table.Th>Why it is listed</Table.Th>
                <Table.Th>Run as</Table.Th>
                <Table.Th>Cron</Table.Th>
                <Table.Th ta="right">Recipients</Table.Th>
                <Table.Th>Last run</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.scheduleId}>
                  <Table.Td><Text size="sm" fw={500}>{row.scheduleName}</Text></Table.Td>
                  <Table.Td>
                    <Stack gap={2} align="flex-start">
                      <ReportButton name={row.reportName} reportId={row.reportId} tab="scheduling" />
                      <Text size="xs" c="dimmed">{row.reportStatus}</Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      {row.reasons.map((reason) => (
                        <Badge key={reason} size="sm" variant="light" color="gray">{scheduleReasonLabel(reason)}</Badge>
                      ))}
                    </Group>
                  </Table.Td>
                  <Table.Td><PrincipalCell displayName={row.runAsDisplayName} id={row.runAsUserId} /></Table.Td>
                  <Table.Td>
                    <Stack gap={2}>
                      <Code>{row.cronExpression}</Code>
                      <Text size="xs" c="dimmed">{row.timeZoneId}</Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    <Text size="sm" c={row.recipientCount == null ? 'dimmed' : undefined}>{recipientLabel(row.recipientCount)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={2}>
                      <Text size="sm">{lastRunLabel(row.lastRunAt)}</Text>
                      {row.lastRunStatus && <Text size="xs" c="dimmed">{row.lastRunStatus}</Text>}
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </FindingSection>
  )
}

// ---------------------------------------------------------------- (7) inactivity

function InactiveSection({
  view, response, thresholdDays,
}: {
  view: SectionView<InactiveGrantHolder>
  response: GovernanceOverview | undefined
  thresholdDays: number | null
}) {
  const withheld = response ? inactivityWithheldNotice(response) : null
  const split = inactiveSplitText(response)
  return (
    <FindingSection
      finding="inactive"
      title="Inactive grant holders"
      sectionName="inactive grant holders"
      subtitle={`People holding live access who ran nothing, interactively or by export, in the last ${thresholdDays ?? NOT_MEASURED} days — grants to people; on reports that cannot run, counted apart. The weakest finding here, and not a verdict that anyone has left.${split ? ` ${split}` : ''}`}
      view={view}
      notComputed={
        <Text size="sm" c="dimmed">
          {withheld ?? `${NOT_MEASURED} Not computed by this response, so nothing is known here — which is not the same as none.`}
        </Text>
      }
    >
      {(rows) => (
        <Table.ScrollContainer minWidth={760}>
          <Table striped highlightOnHover verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Principal</Table.Th>
                <Table.Th ta="right">Report grants</Table.Th>
                <Table.Th ta="right">On reports that cannot run (view-only)</Table.Th>
                <Table.Th ta="right">Workspace memberships</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.principalId}>
                  <Table.Td><PrincipalCell displayName={row.displayName} id={row.principalId} /></Table.Td>
                  <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{exactNumber(row.reportGrantCount)}</Table.Td>
                  {/* `?? null`: an API predating the field did not count these, which is a dash, not 0. */}
                  <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{countOrDash(row.notRunnableReportGrantCount ?? null)}</Table.Td>
                  <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{exactNumber(row.workspaceMembershipCount)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </FindingSection>
  )
}
